import { PhoneNumber } from "@/Common/Api/Contacts/Dto/PhoneNumber";
import { MedfarDictionariesValidationRule } from "@/Common/Api/MedfarDictionaries/Dto/Responses/MedfarDictionariesValidationRule";
import { ValidationHelper } from "@/Common/Helpers/ValidationHelper";
import { ContactPointType } from "@/Common/Models/Contacts/Enums/ContactPointType";
import { PhoneType } from "@/Common/Models/Contacts/Enums/PhoneType";
import { MedfarDictionariesValidationRuleTypeEnum } from "@/Common/Models/MedfarDictionaries/Enums/MedfarDictionariesValidationRuleTypeEnum";
import { Patient } from "@/Common/Models/Patient/Patient";
import { MedfarDictionariesValidationHelper } from "./MedfarDictionariesValidationHelper";

interface IPhoneNumberData {
    number: string;
    countryId: string;
    geoStateId?: string;
    extension?: string;
}

interface IPhoneIconVariantData {
    variant: "warning" | "success",
    tooltipLocalizationKey: string
}

class PhoneNumberHelper {
    private static cachedValidationHelper: ValidationHelper;
    private static cachedMedfarDictionariesValidationHelper: MedfarDictionariesValidationHelper;

    public static format(phoneNumber: string, geoCountryId: string, geoStateId?: string, extension?: string): string {
        if (IsNullOrWhiteSpace(phoneNumber))
            return "";

        if (IsNullOrWhiteSpace(geoCountryId))
            return phoneNumber;

        const validationRule = this.getValidationRule(geoCountryId, geoStateId);

        if (!validationRule)
            return phoneNumber;

        const displayFormatSelector = validationRule.DisplayFormatSelectorPatern ?? "";
        const displayFormatSubstitution = validationRule.DisplayFormatSubstitution ?? "";

        let formattedNumber = this.cachedValidationHelper
            .getFormatedString(phoneNumber, displayFormatSelector, displayFormatSubstitution);

        if (!IsNullOrWhiteSpace(extension))
            formattedNumber += ` ${cacheStore.GetString("ExtLabel")} ${extension}`;

        return formattedNumber;
    }

    public static formatDirectoryNumber(directoryNumber: IPhoneNumberData | null): string {
        return this.format(directoryNumber?.number, directoryNumber?.countryId, directoryNumber?.geoStateId, directoryNumber?.extension);
    }

    public static validate(phoneNumber: string, geoCountryId: string, geoStateId?: string): boolean {
        if (IsNullOrWhiteSpace(phoneNumber) || IsNullOrWhiteSpace(geoCountryId))
            return false;

        const validationRule = this.getValidationRule(geoCountryId, geoStateId);
        const validationRegex = validationRule ? validationRule.ValidationRegexPattern : "";
        const regex = new RegExp(validationRegex, "i");
        return !IsNullOrWhiteSpace(phoneNumber) && regex.test(phoneNumber);
    }

    public static getValidationRule(geoCountryId: string, geoStateId?: string): MedfarDictionariesValidationRule {
        if (IsNullOrWhiteSpace(geoCountryId))
            return null;

        this.cachedValidationHelper ??= new ValidationHelper();
        this.cachedMedfarDictionariesValidationHelper ??= new MedfarDictionariesValidationHelper(cacheStore.MedfarDictionariesValidationRules);

        return this.cachedMedfarDictionariesValidationHelper
            .getValidationRuleForCountryAndRegion(MedfarDictionariesValidationRuleTypeEnum.PhoneNumber, geoCountryId, geoStateId);
    }

    public static validateWalkMePhoneNumber(contactPointTypeLookupId: string, phoneTypeLookupId: string): boolean {
        const workContactPointTypeId = cacheStore.getLookupByContactPointType(ContactPointType.Work)?.LookupID;
        const personalContactPointTypeId = cacheStore.getLookupByContactPointType(ContactPointType.Personal)?.LookupID;
        const otherContactPointTypeId = cacheStore.getLookupByContactPointType(ContactPointType.Other)?.LookupID;

        if (
            contactPointTypeLookupId !== workContactPointTypeId &&
            contactPointTypeLookupId !== personalContactPointTypeId &&
            contactPointTypeLookupId !== otherContactPointTypeId
        ) {
            return false;
        }

        const mobileTypeId = cacheStore.getLookupByPhoneType(PhoneType.Mobile)?.LookupID;
        if (phoneTypeLookupId !== mobileTypeId) {
            return false;
        }

        return true;
    }

    public static getPhoneIconVariantByPatient(currentPhone: PhoneNumber, patient: Patient): IPhoneIconVariantData {
        return this.getPhoneIconVariant(
            currentPhone?.NotificationsEnabled,
            currentPhone?.PhoneTypeLookupId === cacheStore.getLookupByPhoneType(PhoneType.Mobile)?.LookupID && !patient.Phones?.any(phone => phone.NotificationsEnabled),
            patient.Emails?.any(email => email.NotificationsEnabled),
            patient.PreferredPharmacy?.getPharmacyInfo()?.IsMylePharmacy
        );
    }

    public static getPhoneIconVariant(
        isCurrentPhoneNotifiable: boolean,
        isAnyUnnotifiablePhone: boolean,
        isAnyEmailNotifiable: boolean,
        isMylePreferredPharmacy: boolean
    ): IPhoneIconVariantData {
        if (isCurrentPhoneNotifiable)
            return {
                variant: "success",
                tooltipLocalizationKey: "PatientBanner_PhoneNotificationStatusSuccessLabel"
            };

        if (isAnyUnnotifiablePhone && isMylePreferredPharmacy && !isAnyEmailNotifiable)
            return {
                variant: "warning",
                tooltipLocalizationKey: "PatientBanner_PhoneNotificationStatusWarningLabel"
            };

        return null;
    }
}

export { IPhoneNumberData, PhoneNumberHelper };
